import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { updateClientContact, ClientContactDTO, getClientContactById } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsDialog,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { object, string } from 'yup';

import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const getValidationSchema = (type: 'email' | 'phone') => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const phoneNumberRegex =
    /^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/;

  return object().shape({
    value:
      type === 'email'
        ? string()
            .required('Email is required')
            .matches(emailRegex, 'Please enter a valid email address')
        : string()
            .required('Phone number is required')
            .matches(phoneNumberRegex, 'Please enter a valid phone number'),
  });
};

interface AddUpdateContactDetailsProps {
  type: 'email' | 'phone';
  currentValue?: string;
  postSubmit: () => void;
  onCancel: () => void;
  contactId: string;
}

const AddUpdateContactDetails = ({
  type,
  currentValue = '',
  postSubmit,
  onCancel,
  contactId,
}: AddUpdateContactDetailsProps) => {
  const [isLoading, setLoading] = useState(false);
  const [contact, setContact] = useState<ClientContactDTO | null>(null);
  const [fetchingContact, setFetchingContact] = useState(true);
  const formMethods = useForm({
    resolver: yupResolver(getValidationSchema(type)),
    defaultValues: {
      value: currentValue,
    },
  });
  const setNotification = useNotificationStore((state) => state.setNotification);

  const isAddMode = !currentValue;

  useEffect(() => {
    const fetchContact = async () => {
      try {
        setFetchingContact(true);
        const response = await getClientContactById(contactId);
        if (response.status === 200) {
          setContact(response.data.data);
        } else {
          setNotification(failed('Failed to fetch contact details'));
          onCancel();
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
        setNotification(failed('Failed to fetch contact details'));
        onCancel();
      } finally {
        setFetchingContact(false);
      }
    };

    if (contactId) {
      fetchContact();
    }
  }, [contactId, setNotification, onCancel]);

  const onSubmit = async (data: { value: string }) => {
    if (!contact) {
      setNotification(failed('Contact data not available'));
      return;
    }

    setLoading(true);
    try {
      const updatedContact: ClientContactDTO = {
        ...contact,
        [type === 'email' ? 'emailAddress' : 'phoneNumber']: data.value,
      };
      const response = await updateClientContact(updatedContact);

      if (response.status === 200) {
        setNotification(
          success(
            `${type === 'email' ? 'Email' : 'Phone number'} ${
              isAddMode ? 'added' : 'updated'
            } successfully!`
          )
        );

        postSubmit();
      } else {
        throw new Error('Failed to update contact');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      setNotification(
        failed(`Failed to ${isAddMode ? 'add' : 'update'} ${type}. Please try again.`)
      );
    }
    setLoading(false);
  };

  const getTitle = () => {
    const action = isAddMode ? 'Add' : 'Update';
    const field = type === 'email' ? 'Email' : 'Phone Number';
    return `${action} ${field}`;
  };

  const getFieldLabel = () => (type === 'email' ? 'Email Address' : 'Phone Number');

  const getPlaceholder = () =>
    type === 'email' ? 'Enter email address' : 'Enter phone number (e.g., +91 9876543210)';

  const getInputType = () => (type === 'email' ? 'email' : 'tel');

  if (fetchingContact) {
    return (
      <WsDialog onClose={onCancel} openDialog={true}>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
          <Box ml={2}>
            <WsTypography variant={WsTypoVariants.BODY1}>Loading contact details...</WsTypography>
          </Box>
        </Box>
      </WsDialog>
    );
  }

  if (!contact) {
    return (
      <WsDialog onClose={onCancel} openDialog={true}>
        <Box p={4}>
          <WsTypography variant={WsTypoVariants.BODY1} color={WsColors.Red as any}>
            Failed to load contact details. Please try again.
          </WsTypography>
        </Box>
      </WsDialog>
    );
  }

  return (
    <WsDialog onClose={onCancel} openDialog={true}>
      <FormProvider {...formMethods}>
        <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <DialogTitle>{getTitle()}</DialogTitle>
            <IconButton
              onClick={onCancel}
              sx={{
                color: WsColors.GreyBlack,
                mr: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider orientation="horizontal" />
          <DialogContent sx={{ mb: 2 }}>
            <ControlledTextInput
              name="value"
              fullWidth
              type={getInputType()}
              labelText={`${getFieldLabel()} *`}
              placeholder={getPlaceholder()}
              disabled={isLoading}
            />
          </DialogContent>
          <Divider orientation="horizontal" />
          <DialogActions sx={{ mr: 2, mb: 1 }}>
            <WsButton
              type="submit"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default AddUpdateContactDetails;
